import { useState, useEffect, useCallback } from 'react'

const LS_KEY = 'training-platform'

export const PLATFORM_IDS = ['mac', 'windows']

/**
 * Remembers whether the trainee is on a Mac or Windows.
 *
 * The install steps genuinely diverge — different terminal, different Homebrew
 * path, different shell config file — and showing both sets at once is how
 * people run the wrong one. The choice is stored so it carries across the
 * pages of a lesson and across sessions.
 *
 * Returns [platform, setPlatform]. `platform` is null until they choose, which
 * callers should treat as "ask them first" rather than defaulting to Mac —
 * silently assuming Mac is what leaves Windows users stuck.
 *
 * Lives here rather than beside the component so the component file exports
 * only components, which is what keeps fast refresh working.
 */
export function usePlatform() {
  const [platform, setPlatformState] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_KEY)
      return PLATFORM_IDS.includes(saved) ? saved : null
    } catch {
      return null
    }
  })

  const setPlatform = useCallback((id) => {
    setPlatformState(id)
    try { localStorage.setItem(LS_KEY, id) } catch { /* private mode */ }
  }, [])

  // Keep two open tabs in step.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === LS_KEY && PLATFORM_IDS.includes(e.newValue)) {
        setPlatformState(e.newValue)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  return [platform, setPlatform]
}
